/**
 * Uma série diária em barras, sem biblioteca de gráfico.
 *
 * **O QUE ESTA TELA PRECISA DESENHAR DIFERENTE.** `valor` nulo é **sem dado
 * nenhum** no período — o sinal de que quem alimenta a série parou. Zero é "o
 * sistema estava vivo e isto não aconteceu". Desenhar os dois como barra
 * rasteira apaga a diferença, e é justamente ela que denuncia consumidor parado.
 * Aqui o nulo vira uma faixa tracejada com o rótulo "sem dado".
 */
import { cn } from '@/lib/utils'
import type { Ponto } from '@/schemas/painel'

interface Props {
  pontos: Ponto[]
  /** Como escrever o valor no rótulo (moeda, inteiro...). */
  formatar: (valor: number) => string
  legenda: string
  className?: string
}

function dia(instante: string): string {
  const d = new Date(instante)
  return `${String(d.getUTCDate()).padStart(2, '0')}/${String(
    d.getUTCMonth() + 1
  ).padStart(2, '0')}`
}

export function SerieEmBarras({ pontos, formatar, legenda, className }: Props) {
  if (pontos.length === 0) {
    return (
      <p className={cn('py-8 text-center text-sm text-gray-500', className)}>
        Sem período para desenhar.
      </p>
    )
  }

  const maximo = Math.max(...pontos.map((p) => p.valor ?? 0), 0)
  const teto = maximo > 0 ? maximo : 1

  return (
    <div className={className}>
      {/* ⚠️ **`items-end` AQUI FAZIA O GRÁFICO SUMIR.** Com ele, cada coluna
          encolhe até a altura do conteúdo, e a barra com `height: X%` calcula
          contra um pai sem altura resolvida — resultado zero, gráfico em
          branco nas duas telas que usam este componente. As colunas precisam
          esticar (`items-stretch`, que é o padrão) para que a porcentagem tenha
          contra o que ser medida. Achado na validação visual do PR #601. */}
      <div className="flex h-40 items-stretch gap-1">
        {pontos.map((ponto) => {
          const semDado = ponto.valor === null
          const altura = semDado ? 100 : Math.max((ponto.valor! / teto) * 100, 2)
          return (
            <div
              key={ponto.instante}
              className="group relative flex flex-1 flex-col justify-end"
              title={
                semDado
                  ? `${dia(ponto.instante)} — sem dado`
                  : `${dia(ponto.instante)} — ${formatar(ponto.valor!)}`
              }
            >
              <div
                className={cn(
                  'rounded-t',
                  semDado
                    ? 'border border-dashed border-amber-500/50 bg-amber-500/5'
                    : 'bg-gradient-to-t from-indigo-600/70 to-indigo-400'
                )}
                style={{ height: `${altura}%` }}
              />
            </div>
          )
        })}
      </div>
      <div className="mt-2 flex justify-between text-xs text-gray-500">
        <span>{dia(pontos[0].instante)}</span>
        <span>{legenda}</span>
        <span>{dia(pontos[pontos.length - 1].instante)}</span>
      </div>
      {pontos.some((p) => p.valor === null) && (
        <p className="mt-2 text-xs text-amber-400/80">
          As faixas tracejadas são períodos <strong>sem dado nenhum</strong> — que
          não é o mesmo que zero.
        </p>
      )}
    </div>
  )
}
